import { ReactElement } from "react";
import { Box, Grid, SxProps, Theme, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShallowRound } from "../models/round";
import { CouponShare } from "../models/couponshares";
import useCouponShares from "../couponShares/useCouponShares";
import { useQuery } from "react-query";
import fetchApi from "../backend/fetchApi";
import { usePlayer } from "../player/playerContext";
import { PoolXNumberFormat } from "../common/CurrencyFormatter";
import { Button } from "../reusable-components";
import { getTheGameMatchName, TheGameManifest } from "../roundview/components/thegame/thegamemanifest";
import useTheGameManifest from "../roundview/components/thegame/usethegamemanifest";
import RoundBoxWithButton from "./roundboxwithbutton";
import { LiveBadge, PrizePlanTable } from "./roundboxsettledcontent";
import { bestRowNow, CORRECT, getMarketStates, getPot, MarketResult, ProgressCells, WRONG } from "../roundview/components/thegame/thegamecommon";

// Same text styles as RoundBoxContent so the tile matches the normal round boxes.
const smallText: SxProps<Theme> = {
  fontSize: "14px",
  lineHeight: "21px",
  color: "var(--font-color-supportive)",
  fontWeight: 400,
  fontFamily: "var(--font-title)",
};

const smallTitle: SxProps<Theme> = {
  fontSize: "18px",
  lineHeight: "24px",
  color: "var(--text-black)",
  fontWeight: 400,
  fontFamily: "var(--font-title)",
};

const bigTitle: SxProps<Theme> = {
  fontSize: "24px",
  lineHeight: "32px",
  color: "var(--text-black)",
  fontWeight: 700,
  fontFamily: "var(--font-title)",
};

// Matches the height of RoundBoxSettledContent so result tiles line up.
const liveStatsSx: SxProps<Theme> = { marginTop: "5px", marginBottom: "12px", gap: "6px", position: "relative" };

const cellSxByResult: Record<MarketResult, SxProps<Theme>> = {
  open: {},
  won: { background: CORRECT },
  hit: { background: CORRECT, opacity: 0.45 },
  miss: { background: WRONG },
  lost: { background: WRONG },
};

function Stat({ label, value, valueSx = smallTitle }: { label: string; value: string; valueSx?: SxProps<Theme> }): ReactElement {
  return (
    <Grid container alignItems="center" direction="column">
      <Typography sx={smallText}>{label}</Typography>
      <Typography sx={valueSx}>{value}</Typography>
    </Grid>
  );
}

function TileButton({ to, label }: { to: string; label: string }): ReactElement {
  return (
    <Grid container direction="row" justifyContent="space-evenly" sx={{ marginTop: "5px", marginBottom: "25px" }}>
      <Grid item>
        <Link to={to}>
          <Button>{label}</Button>
        </Link>
      </Grid>
    </Grid>
  );
}

function LiveContent({ round, manifest }: { round: ShallowRound; manifest: TheGameManifest }): ReactElement {
  const { t } = useTranslation();
  const [player] = usePlayer() as [any, any];
  const [couponSharesResponse] = useCouponShares(true, false, 0, 50);

  const myShareIds = (couponSharesResponse?.couponShares ?? []).filter((cs) => cs.coupon?.roundId === round.id).map((cs) => cs.id);
  const sharesUrl: string | undefined = player?._links?.couponShares?.href;
  // The list endpoint omits bets, so each share is fetched individually.
  const { data: fullShares } = useQuery<CouponShare[], unknown>(
    ["theGameTileCouponShares", round.id, ...myShareIds],
    () => Promise.all(myShareIds.map((id) => fetchApi(`${sharesUrl}/${id}`).then((r) => r.json()))),
    { enabled: !!sharesUrl && myShareIds.length > 0 }
  );

  const myShares: CouponShare[] = (fullShares ?? []).filter((cs) => !!cs?.coupon);
  const bets = myShares.flatMap((cs) => cs.coupon?.coupon?.bets ?? []);
  // Cells follow the bet behind "best row", otherwise picks from separate coupons merge into one impossible row.
  const bestBet = bets.reduce<typeof bets[number] | undefined>(
    (best, bet) => (!best || bestRowNow(round, [bet]) > bestRowNow(round, [best]) ? bet : best),
    undefined
  );
  const states = getMarketStates(round, manifest, bestBet ? [bestBet] : []);
  const numLegs = states.length;

  if (myShares.length > 0) {
    const totalRows = myShares.reduce((sum, cs) => sum + (cs.coupon?.totalRows ?? 0), 0);
    const totalStake = myShares.reduce((sum, cs) => sum + Number(cs.stake.amount), 0);
    const nf = PoolXNumberFormat(t("locale"), { style: "currency", currency: myShares[0].stake.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return (
      <>
        <Grid container direction="column" alignItems="center" sx={liveStatsSx}>
          <LiveBadge theme={window.theme} t={t} />
          <Stat label={t("your_best_row_now")} value={`${bestRowNow(round, bets)}/${numLegs}`} valueSx={{ ...(bigTitle as object), color: CORRECT, lineHeight: "28px" } as SxProps<Theme>} />
          <Box sx={{ width: "70%" }}>
            <ProgressCells cells={states.map((s) => cellSxByResult[s.result])} />
          </Box>
          <Typography sx={smallText}>
            {totalRows} {t("rows")} · {nf.format(totalStake)}
          </Typography>
        </Grid>
        <PrizePlanTable round={round} theme={window.theme} t={t} />
        <TileButton to={`/coupon/${myShares[0].id}`} label={t("follow_my_coupon")} />
      </>
    );
  }

  const pot = getPot(round, t, manifest.markets.length);
  const numDecided = states.filter((s) => s.decided).length;

  return (
    <>
      <Grid container direction="column" alignItems="center" sx={liveStatsSx}>
        <LiveBadge theme={window.theme} t={t} />
        <Stat label={pot.label} value={pot.text} valueSx={bigTitle} />
        <Stat label={t("markets_settled_label")} value={t("n_of_total", { done: numDecided, total: numLegs })} />
        <Box sx={{ width: "70%" }}>
          <ProgressCells cells={states.map((s) => (s.decided ? { background: CORRECT } : {}))} />
        </Box>
      </Grid>
      <PrizePlanTable round={round} theme={window.theme} t={t} />
      <TileButton to={`/rounds/${round.id}`} label={t("watch_live")} />
    </>
  );
}

interface TheGameTileProps {
  round: ShallowRound;
}

export default function TheGameTile({ round }: TheGameTileProps): ReactElement | null {
  const { t } = useTranslation();
  const isOpen = round.status === "ACTIVE" && new Date(round.openTo) > new Date();
  const { manifest, detailedRound } = useTheGameManifest(round, isOpen ? undefined : 30000);
  if (!manifest) {
    return null;
  }
  return (
    <RoundBoxWithButton round={round} t={t} title={getTheGameMatchName(manifest)}>
      {isOpen ? undefined : <LiveContent round={(detailedRound ?? round) as ShallowRound} manifest={manifest} />}
    </RoundBoxWithButton>
  );
}
